import { useEffect, useState } from "react";
import api from "../lib/api";
import { MyWeekReport } from "../components/PayrollWeek";
import { toast } from "sonner";
import { Clock, Plus, Trash2, Pencil, Save } from "lucide-react";
import { Attendance } from "../components/Attendance";
import { Modal, modalBtnPrimary, modalBtnSecondary } from "../components/Modal";
import { fmtH } from "../lib/format";

const today = () => new Date().toISOString().slice(0, 10);
const fmtD = (d) => (d ? new Date(d + "T12:00:00").toLocaleDateString("cs-CZ", { weekday: "short", day: "numeric", month: "numeric" }) : "");
const inputCls = "w-full min-h-[44px] px-3 py-2 border border-slate-300 bg-white text-sm rounded-sm focus:outline-none focus:ring-2 focus:ring-orange-600";

export default function MyHours() {
    const [data, setData] = useState(null);
    const [jobs, setJobs] = useState([]);
    const [view, setView] = useState("days");
    const [form, setForm] = useState(null);

    const load = () => api.get("/craftsmen/me").then((r) => setData(r.data)).catch((e) => toast.error(e.response?.data?.detail || "Načtení hodin selhalo"));
    useEffect(() => { load(); api.get("/jobs").then((r) => setJobs(r.data || [])).catch(() => {}); }, []);

    const save = async () => {
        if (!form.job_id) { toast.error("Vyberte zakázku"); return; }
        if (!(Number(form.hours) > 0)) { toast.error("Zadejte hodiny"); return; }
        const payload = { job_id: form.job_id, date: form.date, hours: Number(form.hours), notes: form.notes || "", name: data?.card?.name || "" };
        try {
            if (form.id) await api.put(`/craftsmen/${form.id}`, payload); else await api.post("/craftsmen", payload);
            toast.success("Hodiny uloženy"); setForm(null); load();
        } catch (e) { toast.error(e.response?.data?.detail || "Uložení selhalo"); }
    };
    const del = async (id) => {
        if (!window.confirm("Smazat zápis hodin?")) return;
        try { await api.delete(`/craftsmen/${id}`); load(); } catch (e) { toast.error(e.response?.data?.detail || "Smazání selhalo"); }
    };

    return (
        <div className="px-4 sm:px-10 py-6 sm:py-10 max-w-4xl mx-auto animate-fade-in" data-testid="my-hours-page">
            <div className="flex items-end justify-between mb-6 flex-wrap gap-3">
                <div>
                    <div className="label-eyebrow mb-2">Řemeslník</div>
                    <h1 className="font-display text-4xl font-black tracking-tighter text-slate-900">Hodiny</h1>
                    <p className="text-sm text-slate-600 mt-2">{data?.card?.name ? `Karta: ${data.card.name}` : "Příchod/odchod na stavbě nebo ruční zápis hodin."}</p>
                </div>
                <button onClick={() => setForm({ job_id: "", date: today(), hours: 8, notes: "" })} data-testid="new-hours-button"
                        className="inline-flex items-center gap-2 bg-orange-600 hover:bg-orange-700 text-white px-5 min-h-[44px] text-sm font-semibold uppercase tracking-wider rounded-sm">
                    <Plus className="w-4 h-4" /> Zapsat hodiny
                </button>
            </div>

            <div className="mb-6"><Attendance jobs={jobs} solo onSaved={load} /></div>

            <div className="grid grid-cols-2 gap-3 mb-6">
                <Stat label="Celkem hodin" value={fmtH(data?.total_hours)} testid="my-total-hours" />
                <Stat label="Zápisů" value={data?.entries?.length ?? 0} />
            </div>

            <div className="flex gap-1 border-b-2 border-slate-900 mb-3" data-testid="hours-view-tabs">
                {[["days", "Po dnech"], ["weeks", "Po týdnech"], ["entries", "Zápisy"]].map(([k, l]) => (
                    <button key={k} onClick={() => setView(k)} data-testid={`hours-tab-${k}`}
                            className={`px-4 py-2.5 text-xs font-semibold uppercase tracking-wider border-b-2 -mb-0.5 ${view === k ? "border-orange-600 text-slate-900 bg-orange-50" : "border-transparent text-slate-500"}`}>{l}</button>
                ))}
            </div>

            <div className="bg-white border border-slate-200">
                {!data ? <div className="p-10 text-center text-slate-400 text-sm">Načítání…</div> : view === "days" ? (
                    data.days.length === 0 ? <Empty /> : data.days.map((d) => (
                        <div key={d.date} className="flex items-center justify-between px-4 sm:px-6 py-3 border-b border-slate-100" data-testid={`day-row-${d.date}`}>
                            <div><div className="font-medium text-slate-900">{fmtD(d.date)}</div><div className="text-xs text-slate-500">{d.jobs.join(", ")}</div></div>
                            <div className="font-display text-lg font-black tabular-data">{fmtH(d.hours)}</div>
                        </div>
                    ))
                ) : view === "weeks" ? (
                    data.weeks.length === 0 ? <Empty /> : data.weeks.map((w) => (
                        <div key={w.key} className="flex items-center justify-between px-4 sm:px-6 py-3 border-b border-slate-100" data-testid={`week-row-${w.key}`}>
                            <div><div className="font-medium text-slate-900">{fmtD(w.key)} – {fmtD(w.end)}</div><div className="text-xs text-slate-500">{w.days.length} dní · {w.jobs.join(", ")}</div></div>
                            <div className="font-display text-lg font-black tabular-data">{fmtH(w.total_hours)}</div>
                        </div>
                    ))
                ) : (
                    data.entries.length === 0 ? <Empty /> : data.entries.map((e) => (
                        <div key={e.id} className="flex items-start gap-3 px-4 sm:px-6 py-3 border-b border-slate-100" data-testid={`entry-row-${e.id}`}>
                            <div className="flex-1 min-w-0">
                                <div className="font-medium text-slate-900">{fmtD(e.date)} · <span className="tabular-data">{fmtH(e.hours)}</span></div>
                                <div className="text-xs text-slate-500">{e.job_title}{e.notes ? ` — ${e.notes}` : ""}{e.source === "attendance" ? " · docházka" : ""}</div>
                                {e.history?.length > 0 && <div className="text-[11px] text-amber-700 mt-0.5">Upraveno {e.history.length}× (naposledy {e.history[e.history.length - 1].by_name})</div>}
                            </div>
                            <button onClick={() => setForm({ id: e.id, job_id: e.job_id, date: e.date, hours: e.hours, notes: e.notes || "" })} className="min-w-[44px] min-h-[44px] flex items-center justify-center text-slate-400 hover:text-orange-600" data-testid={`entry-edit-${e.id}`}><Pencil className="w-4 h-4" /></button>
                            <button onClick={() => del(e.id)} className="min-w-[44px] min-h-[44px] flex items-center justify-center text-slate-400 hover:text-red-600" data-testid={`entry-delete-${e.id}`}><Trash2 className="w-4 h-4" /></button>
                        </div>
                    ))
                )}
            </div>

            {form && (
                <Modal title={form.id ? "Upravit hodiny" : "Zapsat hodiny"} onClose={() => setForm(null)} testid="hours-modal"
                       footer={<><button onClick={() => setForm(null)} className={modalBtnSecondary}>Zrušit</button><button onClick={save} className={modalBtnPrimary} data-testid="save-hours"><Save className="w-3.5 h-3.5" /> Uložit</button></>}>
                    <div className="space-y-4">
                        <div><label className="label-eyebrow block mb-1.5">Zakázka *</label>
                            <select value={form.job_id} onChange={(e) => setForm({ ...form, job_id: e.target.value })} className={inputCls} data-testid="hours-job">
                                <option value="">— vyberte —</option>{jobs.map((j) => <option key={j.id} value={j.id}>{j.title}{j.site_address ? ` · ${j.site_address}` : ""}</option>)}
                            </select></div>
                        <div className="grid grid-cols-2 gap-3">
                            <div><label className="label-eyebrow block mb-1.5">Datum</label><input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className={inputCls} data-testid="hours-date" /></div>
                            <div><label className="label-eyebrow block mb-1.5">Hodiny</label><input type="number" inputMode="decimal" step="0.5" min="0" value={form.hours} onChange={(e) => setForm({ ...form, hours: e.target.value })} className={inputCls} data-testid="hours-hours" /></div>
                        </div>
                        <div><label className="label-eyebrow block mb-1.5">Poznámka</label><input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className={inputCls} data-testid="hours-notes" /></div>
                    </div>
                </Modal>
            )}
            <MyWeekReport />
        </div>
    );
}

const Empty = () => <div className="p-10 text-center text-slate-400"><Clock className="w-10 h-10 mx-auto mb-2 text-slate-300" /><div className="text-sm">Zatím žádné hodiny.</div></div>;
const Stat = ({ label, value, testid }) => <div className="p-4 bg-white border border-slate-200" data-testid={testid}><div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-1">{label}</div><div className="font-display text-xl font-black tabular-data">{value}</div></div>;
